import React, { useState, useEffect, useCallback } from 'react';
import { View, ScrollView, TouchableOpacity, Platform, Linking, Alert, Share } from 'react-native';
import { Text } from '@/components';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ArrowLeft, MapPin, Bell, Camera, Shield, ChevronRight, Check, X as XIcon } from 'lucide-react-native';
import { useRouter } from 'expo-router';
import * as Location from 'expo-location';
import * as Notifications from 'expo-notifications';
import * as ImagePicker from 'expo-image-picker';
import styles from './permissions.styles';
import { collectDiagnosticDump } from '@/utils/diagnostics';
import settingsStyles from '../settings.styles';
import theme from '@/constants/colors';
import { useTranslation } from 'react-i18next';

interface PermissionStatus {
  label: string;
  description: string;
  granted: boolean | null; // null = not applicable on this platform
  icon: React.ReactNode;
  onAction?: () => void;
  actionLabel?: string;
}

export default function PermissionsScreen() {
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const router = useRouter();

  const shareDiagnostics = useCallback(async () => {
    try {
      const dump = await collectDiagnosticDump();
      await Share.share({ message: dump, title: 'TailTrails GPS diagnostics' });
    } catch (e) { /* ignore */ }
  }, []);

  const [locationStatus, setLocationStatus] = useState<boolean | null>(null);
  const [locationAlwaysStatus, setLocationAlwaysStatus] = useState<boolean | null>(null);
  const [notificationStatus, setNotificationStatus] = useState<boolean | null>(null);
  const [cameraStatus, setCameraStatus] = useState<boolean | null>(null);
  const [photoLibraryStatus, setPhotoLibraryStatus] = useState<boolean | null>(null);

  const checkPermissions = useCallback(async () => {
    // Location
    try {
      const { status: locStatus } = await Location.getForegroundPermissionsAsync();
      setLocationStatus(locStatus === 'granted');
      const { status: bgStatus } = await Location.getBackgroundPermissionsAsync();
      setLocationAlwaysStatus(bgStatus === 'granted');
    } catch {
      setLocationStatus(null);
      setLocationAlwaysStatus(null);
    }

    // Notifications
    try {
      const { status } = await Notifications.getPermissionsAsync();
      setNotificationStatus(status === 'granted');
    } catch {
      setNotificationStatus(null);
    }

    // Camera
    try {
      const { status } = await ImagePicker.requestCameraPermissionsAsync();
      setCameraStatus(status === 'granted');
    } catch {
      setCameraStatus(null);
    }

    // Photo Library
    try {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      setPhotoLibraryStatus(status === 'granted');
    } catch {
      setPhotoLibraryStatus(null);
    }
  }, []);

  useEffect(() => {
    checkPermissions();
  }, [checkPermissions]);

  const requestLocationPermission = useCallback(async () => {
    try {
      const { status } = await Location.requestForegroundPermissionsAsync();
      setLocationStatus(status === 'granted');
      if (status !== 'granted') {
        Alert.alert(
          t('permissions.locationPermission'),
          t('permissions.locationMessage'),
          [
            { text: t('common.cancel'), style: 'cancel' },
            { text: t('record.openSettings'), onPress: () => Linking.openSettings() },
          ]
        );
      }
    } catch {
      setLocationStatus(false);
    }
  }, []);

  const requestAlwaysLocationPermission = useCallback(async () => {
    try {
      const { status } = await Location.requestBackgroundPermissionsAsync();
      setLocationAlwaysStatus(status === 'granted');
      if (status !== 'granted') {
        Alert.alert(
          t('permissions.alwaysOnLocation'),
          t('permissions.alwaysOnLocationMessage'),
          [
            { text: t('common.cancel'), style: 'cancel' },
            { text: t('record.openSettings'), onPress: () => Linking.openSettings() },
          ]
        );
      }
    } catch {
      setLocationAlwaysStatus(false);
    }
  }, []);

  const requestNotificationPermission = useCallback(async () => {
    try {
      const { status } = await Notifications.requestPermissionsAsync();
      setNotificationStatus(status === 'granted');
      if (status !== 'granted') {
        Alert.alert(
          t('permissions.notifications'),
          t('permissions.notificationsMessage'),
          [
            { text: t('common.cancel'), style: 'cancel' },
            { text: t('record.openSettings'), onPress: () => Linking.openSettings() },
          ]
        );
      }
    } catch {
      setNotificationStatus(false);
    }
  }, []);

  const requestCameraPermission = useCallback(async () => {
    try {
      const { status } = await ImagePicker.requestCameraPermissionsAsync();
      setCameraStatus(status === 'granted');
      if (status !== 'granted') {
        Alert.alert(
          t('permissions.camera'),
          t('permissions.cameraMessage'),
          [
            { text: t('common.cancel'), style: 'cancel' },
            { text: t('record.openSettings'), onPress: () => Linking.openSettings() },
          ]
        );
      }
    } catch {
      setCameraStatus(false);
    }
  }, []);

  const requestPhotoLibraryPermission = useCallback(async () => {
    try {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      setPhotoLibraryStatus(status === 'granted');
      if (status !== 'granted') {
        Alert.alert(
          t('permissions.photoLibrary'),
          t('permissions.photoLibraryMessage'),
          [
            { text: t('common.cancel'), style: 'cancel' },
            { text: t('record.openSettings'), onPress: () => Linking.openSettings() },
          ]
        );
      }
    } catch {
      setPhotoLibraryStatus(false);
    }
  }, []);

  const permissions: PermissionStatus[] = [
    {
      label: t('permissions.locationWhenInUse'),
      description: t('permissions.locationWhenInUseDescription'),
      granted: locationStatus,
      icon: <MapPin size={22} color={theme.accentPrimary} />,
      onAction: locationStatus ? undefined : requestLocationPermission,
      actionLabel: locationStatus ? t('permissions.granted') : t('permissions.enable'),
    },
    {
      label: t('permissions.locationAlways'),
      description: t('permissions.locationAlwaysDescription'),
      granted: locationAlwaysStatus,
      icon: <MapPin size={22} color={theme.accentPrimary} />,
      onAction: locationAlwaysStatus ? undefined : requestAlwaysLocationPermission,
      actionLabel: locationAlwaysStatus ? t('permissions.granted') : t('permissions.enable'),
    },
    {
      label: t('permissions.notifications'),
      description: t('permissions.notificationsDescription'),
      granted: notificationStatus,
      icon: <Bell size={22} color={theme.accentPrimary} />,
      onAction: notificationStatus ? undefined : requestNotificationPermission,
      actionLabel: notificationStatus ? t('permissions.granted') : t('permissions.enable'),
    },
    {
      label: t('permissions.camera'),
      description: t('permissions.cameraDescription'),
      granted: cameraStatus,
      icon: <Camera size={22} color={theme.accentPrimary} />,
      onAction: cameraStatus ? undefined : requestCameraPermission,
      actionLabel: cameraStatus ? t('permissions.granted') : t('permissions.enable'),
    },
    {
      label: t('permissions.photoLibrary'),
      description: t('permissions.photoLibraryDescription'),
      granted: photoLibraryStatus,
      icon: <Camera size={22} color={theme.accentPrimary} />,
      onAction: photoLibraryStatus ? undefined : requestPhotoLibraryPermission,
      actionLabel: photoLibraryStatus ? t('permissions.granted') : t('permissions.enable'),
    },
  ];

  // Filter out "Always" location on iOS < 14 or on web
  const visiblePermissions = Platform.OS === 'web'
    ? permissions.filter(p => p.label !== t('permissions.locationAlways'))
    : permissions;

  const grantedCount = visiblePermissions.filter(p => p.granted === true).length;
  const totalCount = visiblePermissions.length;

  return (
    <View style={[styles.container, { paddingTop: insets.top }]}>
      <View style={settingsStyles.header}>
        <TouchableOpacity style={settingsStyles.backButton} onPress={() => router.back()}>
          <ArrowLeft size={20} color={theme.accentPrimary} />
        </TouchableOpacity>
        <Text style={settingsStyles.title}>{t('permissions.title')}</Text>
        <View style={{ width: 40 }} />
      </View>

      <ScrollView contentContainerStyle={styles.content}>
        {/* Summary card */}
        <View style={styles.summaryCard}>
          <View style={styles.summaryIconRow}>
            <Shield size={28} color={theme.accentPrimary} />
            <Text style={styles.summaryTitle}>{t('permissions.appPermissions')}</Text>
          </View>
          <Text style={styles.summaryBody}>
            {t('permissions.appPermissionsDescription')}
          </Text>
          <View style={styles.summaryCount}>
            <Text style={styles.summaryCountText}>
              {t('permissions.grantedCount', { granted: grantedCount, total: totalCount })}
            </Text>
          </View>
        </View>

        {/* Diagnostics share — for tracking issues on aggressive OEMs (Samsung etc.) */}
        <TouchableOpacity style={styles.summaryCard} onPress={shareDiagnostics}>
          <View style={styles.summaryIconRow}>
            <MapPin size={28} color={theme.accentPrimary} />
            <Text style={styles.summaryTitle}>{t('permissions.shareDiagnostics')}</Text>
          </View>
          <Text style={styles.summaryBody}>{t('permissions.shareDiagnosticsDescription')}</Text>
        </TouchableOpacity>

        {/* Permission list */}
        {visiblePermissions.map((perm) => (
          <View key={perm.label} style={styles.permCard}>
            <View style={styles.permRow}>
              <View style={styles.permIconContainer}>
                {perm.icon}
              </View>
              <View style={styles.permInfo}>
                <Text style={styles.permLabel}>{perm.label}</Text>
                <Text style={styles.permDescription}>{perm.description}</Text>
              </View>
              <View style={styles.permStatusContainer}>
                {perm.granted === true ? (
                  <View style={[styles.statusBadge, styles.statusGranted]}>
                    <Check size={14} color="#34C759" />
                    <Text style={styles.statusGrantedText}>{t('permissions.on')}</Text>
                  </View>
                ) : perm.granted === false ? (
                  <View style={[styles.statusBadge, styles.statusDenied]}>
                    <XIcon size={14} color="#FF3B30" />
                    <Text style={styles.statusDeniedText}>{t('permissions.off')}</Text>
                  </View>
                ) : (
                  <View style={[styles.statusBadge, styles.statusUnknown]}>
                    <Text style={styles.statusUnknownText}>—</Text>
                  </View>
                )}
              </View>
            </View>
            {perm.granted === false && perm.onAction && (
              <TouchableOpacity style={styles.enableButton} onPress={perm.onAction}>
                <Text style={styles.enableButtonText}>{perm.actionLabel}</Text>
                <ChevronRight size={16} color={theme.backgroundPrimary} />
              </TouchableOpacity>
            )}
          </View>
        ))}

        {/* Open system settings button */}
        <TouchableOpacity style={styles.systemSettingsButton} onPress={() => Linking.openSettings()}>
          <Text style={styles.systemSettingsButtonText}>{t('permissions.openSystemSettings')}</Text>
        </TouchableOpacity>

        <Text style={styles.noteText}>
          {t('permissions.tipPermissionsDenied')}
        </Text>
      </ScrollView>
    </View>
  );
}